import { NavLink, useNavigate } from "react-router-dom";
import { Compass, List } from "lucide-react";
import AddBurgerIcon from "@/components/icons/AddBurgerIcon";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";
import { trackAddBurgerTap } from "@/lib/analytics";

const BottomNav = () => {
  const baseLink = "flex flex-col items-center justify-center gap-1 py-2 flex-1";
  const iconClass = "h-6 w-6";
  const labelClass = "text-[11px]";
  const { session } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-50 border-t border-border bg-card/90 backdrop-blur supports-[backdrop-filter]:bg-card/70" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
      <div className="mx-auto w-full max-w-screen-md lg:max-w-screen-lg xl:max-w-screen-xl px-5 md:px-10 lg:px-24 xl:px-32 2xl:px-48">
        <ul className="flex">
          <li className="flex-1">
            <NavLink 
              to={session?.user?.id ? "/home" : "/auth"} 
              className={({ isActive }) => cn(baseLink, isActive ? "text-primary" : "text-muted-foreground")} 
              aria-label={session?.user?.id ? "Activity" : "Sign up for full access"}
              onClick={(e) => {
                if (!session?.user?.id) {
                  e.preventDefault();
                  toast({
                    variant: "signup",
                    title: "Sign up for BRRGR",
                    description: "Create an account to track your burger journey",
                    action: (
                      <ToastAction 
                        altText="Create account" 
                        onClick={() => navigate('/auth')}
                        className="bg-primary text-primary-foreground border-primary hover:bg-primary/90 focus:ring-primary"
                      >
                        Create account
                      </ToastAction>
                    ),
                    duration: 10000,
                  });
                }
              }}
            >
              <List className={iconClass} />
              <span className={labelClass}>{session?.user?.id ? "Activity" : "Sign Up"}</span>
            </NavLink>
          </li>
          <li className="flex-1">
            <NavLink
              to="/add"
              className={({ isActive }) =>
                cn(
                  "mx-auto w-28 -mt-6 rounded-full shadow-lg ring-1",
                  "flex flex-col items-center justify-center gap-1 py-2",
                  isActive
                    ? "bg-muted text-muted-foreground ring-border pointer-events-none"
                    : "bg-primary text-primary-foreground ring-primary/50"
                )
              }
              onClick={(e) => {
                trackAddBurgerTap('bottom_nav');
                if (!session?.user?.id) {
                  e.preventDefault();
                  toast({
                    variant: "signup",
                    title: "Sign up to add burgers",
                    action: (
                      <ToastAction 
                        altText="Create account" 
                        onClick={() => navigate('/auth')}
                        className="bg-primary text-primary-foreground border-primary hover:bg-primary/90 focus:ring-primary"
                      >
                        Create account
                      </ToastAction>
                    ),
                    duration: 10000,
                  });
                }
              }}
              aria-label="Add Burger"
            >
              <AddBurgerIcon className="h-7 w-7" />
              <span className="text-[11px] font-medium">Add Burger</span>
            </NavLink>
          </li>
          <li className="flex-1">
            <NavLink 
              to={session?.user?.id ? "/discover" : "/leaderboard"} 
              className={({ isActive }) => cn(baseLink, isActive ? "text-primary" : "text-muted-foreground")} 
              aria-label={session?.user?.id ? "Discover" : "Leaderboard"}
            >
              <Compass className={iconClass} />
              <span className={labelClass}>{session?.user?.id ? "Discover" : "Leaderboard"}</span>
            </NavLink>
          </li>
        </ul>
      </div>
    </nav>
  );
};

export default BottomNav;
